import { useState } from "react";
import { motion } from "framer-motion";
import { Expand } from "lucide-react";
import { GALLERY } from "./data";
import { SectionHead, Reveal } from "./Reveal";
import { Lightbox } from "./Lightbox";
import { BeforeAfter } from "./BeforeAfter";
import { EASE } from "./scroll";
import { Pic } from "./Pic";

const LAYOUT = [
  "col-span-2 row-span-2 lg:col-start-1 lg:row-start-1",
  "lg:col-start-3 lg:row-start-1",
  "lg:col-start-4 lg:row-start-1 lg:row-span-2",
  "lg:col-start-3 lg:row-start-2",
  "lg:col-start-1 lg:row-start-3",
  "lg:col-start-2 lg:row-start-3 lg:col-span-2",
  "lg:col-start-4 lg:row-start-3",
];

export const Gallery = () => {
  const [index, setIndex] = useState(null);
  return (
    <section id="referenzen" aria-labelledby="ref-title" className="relative bg-ink2 py-24 sm:py-32 lg:py-40" data-testid="gallery-section">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <SectionHead id="ref-title" eyebrow="Referenzen" title={<>Arbeit, die<br />man sieht.</>} />
          <Reveal className="max-w-md lg:pb-3" delay={0.15}>
            <p className="text-base leading-relaxed text-slate-300 sm:text-lg">Ein Einblick in unser Handwerk – vom Dachstuhl bis zur Kupferkante. Klicken Sie auf ein Bild für die Großansicht.</p>
            <p className="mt-3 text-xs text-slate-400">Platzhalter für echte Projektfotos – Bildunterschrift mit Ort und Leistung folgt.</p>
          </Reveal>
        </div>
        <div className="mt-14 grid auto-rows-[160px] grid-cols-2 gap-3 sm:mt-20 sm:auto-rows-[230px] lg:auto-rows-[250px] lg:grid-cols-4 lg:gap-4">
          {GALLERY.map((g, i) => (
            <motion.button
              key={g.img}
              type="button"
              onClick={() => setIndex(i)}
              className={`group relative overflow-hidden bg-ink text-left ${LAYOUT[i]}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.8, ease: EASE, delay: (i % 4) * 0.07 }}
              aria-label={`${g.title} vergrößern`}
              data-testid={`gallery-item-${i}`}
            >
              <Pic name={g.img} alt={g.alt} sizes={i === 0 || i === 5 ? "(max-width: 1024px) 100vw, 50vw" : "(max-width: 1024px) 50vw, 25vw"} className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
              <span className="absolute inset-0 bg-gradient-to-t from-ink/95 via-ink/35 to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-100" />
              <span className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 p-4 sm:p-6">
                <span>
                  <span className="inline-block bg-ink/80 px-2 py-1 text-[0.75rem] font-semibold uppercase tracking-[0.2em] text-white backdrop-blur"><span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-terra align-middle" />{g.cat} · {g.place}</span>
                  <span className="mt-2 hidden font-display text-xl font-bold uppercase leading-tight text-white sm:block lg:text-2xl">{g.title}</span>
                </span>
                <span className="grid h-10 w-10 shrink-0 scale-75 place-items-center rounded-full bg-terra-deep text-white opacity-0 transition-[opacity,transform] duration-500 group-hover:scale-100 group-hover:opacity-100"><Expand size={16} /></span>
              </span>
            </motion.button>
          ))}
        </div>
        <BeforeAfter />
      </div>
      <Lightbox items={GALLERY} index={index} setIndex={setIndex} />
    </section>
  );
};
